import { StringData } from "../..";
import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";

/** Attribute selectors for position tracker elements */
const ATTR_VAL = "data-val";
const ATTR_VAL_PCT = "data-val-pct";
const ATTR_DIR = "data-dir";

/**
 * Tracker module that broadcasts scroll position to elements with data attributes.
 * Elements with `data-val`, `data-val-pct`, or `data-dir` will receive updates.
 * Also creates an optional debug display element.
 */
export class StringPositionTracker extends StringModule {
  private displayElement: HTMLDivElement | null = null;

  /** Cached elements by attribute type */
  private valElements: Set<HTMLElement> = new Set();
  private valPctElements: Set<HTMLElement> = new Set();
  private dirElements: Set<HTMLElement> = new Set();

  /** MutationObserver for DOM changes */
  private observer: MutationObserver | null = null;

  /** Last known values to avoid redundant updates */
  private lastVal = -1;
  private lastValPct = -1;
  private lastDir = "";

  /** Previous scroll position for direction detection */
  private previousCurrent = 0;

  /** Timeout for resetting direction to idle state */
  private idleTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(context: StringContext) {
    super(context);
    this._type = 2;
  }

  /**
   * Called on start — creates debug element and scans for tracked elements.
   */
  onInit(): void {
    if (this.data.system.positionTracker) {
      this.createDisplayElement();
    }

    // Listen for visibility toggle
    this.events.on("tracker:position:visible", this.onVisibilityChange.bind(this));

    this.scanElements();
    this.observeDOM();
  }

  /**
   * Called on scroll — updates all tracked elements with position data.
   */
  onScroll(data: StringData): void {
    const current = data.scroll.current;
    const target = data.scroll.target;
    const contentHeight = data.viewport.contentHeight;
    const windowHeight = data.viewport.windowHeight;

    const val = Math.round(current);
    const maxScroll = Math.max(1, contentHeight - windowHeight);
    const valPct = Math.round((current / maxScroll) * 100);

    // Determine scroll direction
    let direction: string;
    if (current !== target) {
      // Smooth scroll: compare current vs target (lerping)
      direction = current < target ? "↓" : "↑";
    } else if (current !== this.previousCurrent) {
      // Default scroll: compare with previous position
      direction = current > this.previousCurrent ? "↓" : "↑";
    } else {
      // No movement
      direction = this.lastDir || "•";
    }

    this.previousCurrent = current;

    // Reset idle timeout - will set direction to neutral after scroll stops
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    if (direction !== "•") {
      this.idleTimeout = setTimeout(() => {
        this.setDirection("•");
      }, 150);
    }

    // Update data-val elements
    if (val !== this.lastVal) {
      this.lastVal = val;
      const valStr = String(val);
      for (const el of this.valElements) {
        if (el.isConnected) {
          el.setAttribute(ATTR_VAL, valStr);
        }
      }
      if (this.displayElement) {
        this.displayElement.setAttribute(ATTR_VAL, valStr);
      }
    }

    // Update data-val-pct elements
    if (valPct !== this.lastValPct) {
      this.lastValPct = valPct;
      const pctStr = String(valPct);
      for (const el of this.valPctElements) {
        if (el.isConnected) {
          el.setAttribute(ATTR_VAL_PCT, pctStr);
        }
      }
      if (this.displayElement) {
        this.displayElement.setAttribute(ATTR_VAL_PCT, pctStr);
      }
    }

    // Update data-dir elements
    this.setDirection(direction);

    this.events.emit("scroll-position", { val, valPct, direction });
  }

  /**
   * Updates direction on all tracked elements.
   */
  private setDirection(direction: string): void {
    if (direction === this.lastDir) return;
    this.lastDir = direction;

    for (const el of this.dirElements) {
      if (el.isConnected) {
        el.setAttribute(ATTR_DIR, direction);
      }
    }
    if (this.displayElement) {
      this.displayElement.setAttribute(ATTR_DIR, direction);
    }
  }

  /**
   * Cleans up DOM observer and display element.
   */
  destroy(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    this.observer?.disconnect();
    this.removeDisplayElement();
    this.valElements.clear();
    this.valPctElements.clear();
    this.dirElements.clear();
  }

  /**
   * Handles visibility toggle from external API.
   */
  private onVisibilityChange(visible: boolean): void {
    if (visible) {
      this.createDisplayElement();
    } else {
      this.removeDisplayElement();
    }
  }

  /**
   * Removes the display element.
   */
  private removeDisplayElement(): void {
    this.displayElement?.remove();
    this.displayElement = null;
  }

  /**
   * Scans document for elements with tracking attributes.
   */
  private scanElements(): void {
    this.valElements.clear();
    this.valPctElements.clear();
    this.dirElements.clear();

    document.querySelectorAll<HTMLElement>(`[${ATTR_VAL}]`).forEach((el) => {
      if (el !== this.displayElement) this.valElements.add(el);
    });

    document.querySelectorAll<HTMLElement>(`[${ATTR_VAL_PCT}]`).forEach((el) => {
      if (el !== this.displayElement) this.valPctElements.add(el);
    });

    document.querySelectorAll<HTMLElement>(`[${ATTR_DIR}]`).forEach((el) => {
      if (el !== this.displayElement) this.dirElements.add(el);
    });
  }

  /**
   * Observes DOM for added/removed elements with tracking attributes.
   */
  private observeDOM(): void {
    this.observer = new MutationObserver((mutations) => {
      let needsRescan = false;

      for (const mutation of mutations) {
        // Check added nodes
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (this.hasTrackingAttr(el)) needsRescan = true;
            // Check descendants
            if (el.querySelector(`[${ATTR_VAL}],[${ATTR_VAL_PCT}],[${ATTR_DIR}]`)) {
              needsRescan = true;
            }
          }
        }

        // Check removed nodes - clean from cache
        for (const node of mutation.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            this.valElements.delete(el);
            this.valPctElements.delete(el);
            this.dirElements.delete(el);
          }
        }
      }

      if (needsRescan) {
        this.scanElements();
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Checks if element has any tracking attribute.
   */
  private hasTrackingAttr(el: HTMLElement): boolean {
    return el.hasAttribute(ATTR_VAL) || el.hasAttribute(ATTR_VAL_PCT) || el.hasAttribute(ATTR_DIR);
  }

  /**
   * Creates and styles the floating position indicator.
   */
  private createDisplayElement(): void {
    if (this.displayElement) return;

    const el = document.createElement("div");

    Object.assign(el.style, {
      position: "fixed",
      bottom: "10px",
      left: "10px",
      backgroundColor: "#000",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.2)",
      padding: "5px 8px",
      fontSize: "12px",
      fontFamily: "monospace",
      zIndex: "1000",
      pointerEvents: "none",
    });

    el.setAttribute(ATTR_DIR, "•");
    el.setAttribute(ATTR_VAL, "0");
    el.setAttribute(ATTR_VAL_PCT, "0");
    document.body.appendChild(el);

    // Inject dynamic CSS
    const styleId = "string-position-tracker-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        [${ATTR_DIR}][${ATTR_VAL}][${ATTR_VAL_PCT}]::before {
          content: attr(${ATTR_DIR}) ' | ' attr(${ATTR_VAL}) 'px (' attr(${ATTR_VAL_PCT}) '%)';
        }
      `;
      document.head.appendChild(style);
    }

    this.displayElement = el;
  }
}
